import { BankingEntitiesBankAccountNumberFormatEnum } from 'generated/resources/datatable.graphql'
import { SetBankingInformationDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  accountNumberExtraFieldLabel,
  accountNumberLabel,
  bankAccountFormatOptions,
  defaultBankNumberFormat,
} from 'modules/banking/lib/bankAccountNumbersHelpers'
import type Employee from 'modules/employees/store/models/Employee'

type SetBankInformationFormValues = {
  accountNumber: string
  complementaryData: string
  format: BankingEntitiesBankAccountNumberFormatEnum
  employeeId: number
}

export const useBankInfoForm = (employee: Employee, readonly: boolean) => {
  const { editEmployeePersonalBankInfoSubmit } = trackingEvents.employee.personal
  const [setBankingInformation, { loading: submitting }] = useMutation(
    SetBankingInformationDocument,
    {
      extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountNumber,
    }
  )

  const legalEntity = employeeLegalEntity(employee)
  const countryCode = employee.attributes.country?.toString().toLowerCase() ?? ''
  let defaultFormat = employee.attributes.bank_number_format

  if (!defaultFormat) {
    defaultFormat = defaultBankNumberFormat(countryCode)
  } else if (defaultFormat === '') {
    defaultFormat = 'other'
  }

  const defaultValues: SetBankInformationFormValues = {
    accountNumber: employee.attributes.bank_number || '',
    complementaryData: employee.attributes.swift_bic || '',
    format: defaultFormat as BankingEntitiesBankAccountNumberFormatEnum,
    employeeId: employee.id,
  }

  const form = useForm<SetBankInformationFormValues>({
    defaultValues,

    validate: (data) => {
      const errors: FormErrors<SetBankInformationFormValues> = {}
      if (!data.accountNumber) {
        errors.accountNumber = i18n.t('employee_form.bank_info.mandatory')
      }
      if (
        (data.format === 'sort_code_and_account_number' ||
          data.format === 'routing_number_and_account_number' ||
          data.format === 'bank_name_and_account_number') &&
        !data.complementaryData
      ) {
        errors.complementaryData = i18n.t('employee_form.bank_info.mandatory')
      }
      return errors
    },

    onSubmit: async (values) => {
      const variables = {
        accountNumber: values.accountNumber,
        complementaryData: values.complementaryData,
        format: values.format,
        employeeId: employee.id,
      }

      const result = await setBankingInformation({ variables })

      track(editEmployeePersonalBankInfoSubmit, {
        fields_edited: Object.keys(form.touched),
        fields_completed: Object.keys(form.values).filter(
          (key) => !!form.values[key as keyof SetBankInformationFormValues]
        ),
        legal_entity_country: legalEntity.country,
      })

      const handled = handleResult(result, (errors) => {
        form.setErrors({
          accountNumber: errors['accountNumber'],
          complementaryData: errors['complementaryData'],
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('employee_form.bank_info.saved_successfully'),
        })
        form.resetForm({ values })
        employee.set({
          bank_number: values.accountNumber,
          bank_number_format: values.format,
          swift_bic: values.complementaryData,
        })
      } else {
        form.setFieldError('accountNumber', i18n.t('employee_form.bank_info.invalid_format'))

        if (form.values.complementaryData) {
          form.setFieldError('complementaryData', i18n.t('employee_form.bank_info.invalid_format'))
        }

        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
      return handled
    },
  })

  useEffect(() => {
    form.resetForm({
      values: defaultValues,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.attributes.id])

  const hasErrors = Object.values(form.errors).filter((e) => !!e).length > 0
  const accountLabel = accountNumberLabel(form.values.format)
  const extraFieldLabel = accountNumberExtraFieldLabel(form.values.format)
  const showSubmit = !readonly && (form.dirty || hasErrors || submitting)

  return {
    form,
    submitting,
    bankAccountFormatOptions,
    accountLabel,
    extraFieldLabel,
    showSubmit,
  }
}
